/**
 * Like Render - UI State Management
 * Handles like button UI updates and visual feedback
 */

import { notifier } from "../core/notifier/notifier";
import { modalHandler } from "../modal/modal-handler";
import { getOpenerView } from "../core/view";
import { toPositiveIntOrNull } from "../util/id-utils";
import { LikeForm } from "./like-form";

interface ModalHandlerLike {
    updateParentLikeCount?(postId: number, likesCount: number): void;
    updateParentLikeState?(postId: number, isLiked: boolean): void;
}

class LikeRender {
    public form: LikeForm;

    public constructor(form: LikeForm) {
        this.form = form;
    }

    public getLikesCount(button: HTMLElement): number {
        return this.form.getLikesCount(button);
    }

    public updateLikeUI(button: HTMLElement, isLiked: boolean, likesCount: number): void {
        const iconUnliked = "bi-heart";
        const iconLiked = "bi-heart-fill";

        if (isLiked) {
            button.classList.add("liked");
        } else {
            button.classList.remove("liked");
        }

        const icon = this.form.getIconElement(button);
        const countSpan = this.form.getCountElement(button);

        if (icon) {
            icon.className = `bi ${isLiked ? iconLiked : iconUnliked}`;
        }

        if (countSpan) {
            countSpan.textContent = String(likesCount);
        }

        button.title = isLiked ? "Remove like" : "Add like";
        button.setAttribute("aria-pressed", isLiked ? "true" : "false");
        button.dataset.isLiked = String(!!isLiked);

        const scope = button.dataset.likeScope || "post";
        if (scope === "post") {
            const isInElementModal = this.form.isInElementModal(button);
            const modalHandlerLike = modalHandler as ModalHandlerLike | null;
            if (isInElementModal && modalHandlerLike) {
                const postId = toPositiveIntOrNull(button.dataset.postId);
                if (postId !== null) {
                    modalHandlerLike.updateParentLikeCount?.(postId, likesCount);
                    modalHandlerLike.updateParentLikeState?.(postId, isLiked);
                }
            }
        }
    }

    public setLoadingState(button: HTMLElement): void {
        if (button instanceof HTMLButtonElement) {
            button.disabled = true;
        }
        button.classList.add("loading");
    }

    public clearLoadingState(button: HTMLElement): void {
        if (button instanceof HTMLButtonElement) {
            button.disabled = false;
        }
        button.classList.remove("loading");
    }

    public updateParentWindowLikeCount(likesCount: number, isLiked: boolean): void {
        const opener = getOpenerView();
        if (!opener) return;

        try {
            if (opener.closed) return;
        } catch (_error) {
            return;
        }

        try {
            const postId = Number(new URLSearchParams(location.search).get("post_id") || "");
            if (!Number.isInteger(postId) || postId <= 0) return;

            const parentButton = this.form.getParentWindowLikeButton(postId);

            if (parentButton) {
                const parentCountSpan = this.form.getCountElement(parentButton);
                if (parentCountSpan) {
                    parentCountSpan.textContent = String(likesCount);
                }

                if (isLiked) {
                    parentButton.classList.add("liked");
                } else {
                    parentButton.classList.remove("liked");
                }
            }
        } catch (error) {
            console.error("Failed to update parent window like button:", error);
        }
    }

    public showError(message: string): void {
        notifier.error(message);
    }
}

export { LikeRender };
